/**
 * Laser Fan Rig Visualizer - Audio-reactive laser beam fan from bottom center
 *
 * A virtual laser rig emitting a fan of beams that rotate, widen, and snap on beats.
 * Haze, scanlines, and frequency-based color shifting create a concert lighting feel.
 * Overrides draw() with an offscreen canvas trail technique that properly respects
 * the global background controller (solid, gradient, pattern, or image backgrounds).
 *
 * Rendering layers (bottom to top):
 * 1. Trail fade — offscreen canvas composited at reduced opacity over fresh background
 * 2. Haze — translucent ellipses near beam origin
 * 3. Beams — fan of thick lines with glow + core passes (additive blending)
 * 4. Beat flash — white core pass on beat detection
 * 5. Scanlines — pre-rendered pattern composite
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';
import { mapFrequencyData, smoothAmplitudes } from '../lib/audio-utils.js';

// Metadata for manifest generation
export const metadata = {
  name: 'laser-fan-rig',
  displayName: 'Laser Fan Rig',
  contextType: '2d',
};

// Fan geometry constants
const FAN_SPREAD = (150 * Math.PI) / 180; // 150 degrees in radians
const FAN_START = Math.PI + (Math.PI - FAN_SPREAD) / 2; // Start angle (centered upward from bottom)

// Snap decay factor — controls how quickly the beat snap settles
const SNAP_DECAY = 0.85;

// Haze ellipse count
const HAZE_COUNT = 4;

class LaserFanRig extends Base2DVisualizer {
  static name = 'laser-fan-rig';
  static displayName = 'Laser Fan Rig';

  static getConfigUI() {
    return [
      { type: 'range', id: 'beamCount', label: 'Beam Count', min: 4, max: 64, default: 16 },
      { type: 'range', id: 'rotationSpeed', label: 'Rotation Speed', min: 0, max: 100, default: 30, scale: 0.01 },
      { type: 'range', id: 'snapAmount', label: 'Beat Snap', min: 0, max: 100, default: 50, scale: 0.01 },
      { type: 'range', id: 'beamWidth', label: 'Beam Width', min: 1, max: 20, default: 4 },
      { type: 'range', id: 'hazeAmount', label: 'Haze', min: 0, max: 100, default: 40, scale: 0.01 },
      { type: 'range', id: 'scanlineAmount', label: 'Scanlines', min: 0, max: 100, default: 20, scale: 0.01 },
      { type: 'range', id: 'flicker', label: 'Flicker', min: 0, max: 100, default: 30, scale: 0.01 },
      { type: 'range', id: 'glowIntensity', label: 'Glow Intensity', min: 0, max: 100, default: 60, scale: 0.01 },
      { type: 'range', id: 'colorCycleSpeed', label: 'Color Cycle Speed', min: 0, max: 100, default: 30, scale: 0.01 },
      { type: 'range', id: 'brightness', label: 'Brightness', min: 20, max: 100, default: 80, scale: 0.01 },
      { type: 'range', id: 'trailAlpha', label: 'Trail Persistence', min: 1, max: 30, default: 8, scale: 0.01 },
      { type: 'range', id: 'smoothing', label: 'Audio Smoothing', min: 0, max: 95, default: 70, scale: 0.01 },
      { type: 'color', id: 'colorStart', label: 'Color Start', default: '#00ccff' },
      { type: 'color', id: 'colorEnd', label: 'Color End', default: '#ff00cc' },
    ];
  }

  init() {
    // Rotation state
    this.rotationOffset = 0;
    this.snapOffset = 0;
    this.colorCycleOffset = 0;
    this.isFirstFrame = true;

    // Haze positions (pre-allocated, randomized in _initHaze)
    this.hazePositions = new Array(HAZE_COUNT);
    this._initHaze();

    // Scanline offscreen canvas + pattern
    this._scanlineCanvas = null;
    this._scanlinePattern = null;
    this._buildScanlinePattern();

    // Trail offscreen canvas — stores previous frame for fade compositing
    this._trailCanvas = null;
    this._trailCtx = null;

    // Parse color start/end into HSL for beam coloring
    this._colorStartHSL = hexToHSL(this.config.colorStart);
    this._colorEndHSL = hexToHSL(this.config.colorEnd);
  }

  onConfigChange(changed) {
    if (changed.scanlineAmount !== undefined) {
      this._buildScanlinePattern();
    }
    if (changed.colorStart !== undefined) {
      this._colorStartHSL = hexToHSL(this.config.colorStart);
    }
    if (changed.colorEnd !== undefined) {
      this._colorEndHSL = hexToHSL(this.config.colorEnd);
    }
  }

  getElementCount() {
    return this.config.beamCount;
  }

  /**
   * Initialize haze ellipse positions with randomized offsets.
   */
  _initHaze() {
    for (let i = 0; i < HAZE_COUNT; i++) {
      this.hazePositions[i] = {
        xOffset: (Math.random() - 0.5) * 0.3, // fraction of canvas width from center
        yOffset: -Math.random() * 0.15, // slightly above origin
        radiusX: 0.08 + Math.random() * 0.12, // fraction of canvas width
        radiusY: 0.04 + Math.random() * 0.06,
      };
    }
  }

  /**
   * Ensure the trail offscreen canvas matches the main canvas dimensions.
   */
  _ensureTrailCanvas() {
    const { canvas } = this;
    if (!this._trailCanvas || this._trailCanvas.width !== canvas.width || this._trailCanvas.height !== canvas.height) {
      if (typeof OffscreenCanvas !== 'undefined') {
        this._trailCanvas = new OffscreenCanvas(canvas.width, canvas.height);
      } else {
        this._trailCanvas = document.createElement('canvas');
        this._trailCanvas.width = canvas.width;
        this._trailCanvas.height = canvas.height;
      }
      this._trailCtx = this._trailCanvas.getContext('2d');
    }
  }

  /**
   * Build the scanline pattern on an offscreen canvas.
   * Pattern is a 2px-tall strip: 1px transparent, 1px semi-opaque dark.
   */
  _buildScanlinePattern() {
    if (typeof OffscreenCanvas !== 'undefined') {
      this._scanlineCanvas = new OffscreenCanvas(1, 2);
    } else {
      this._scanlineCanvas = document.createElement('canvas');
      this._scanlineCanvas.width = 1;
      this._scanlineCanvas.height = 2;
    }

    const sctx = this._scanlineCanvas.getContext('2d');
    sctx.clearRect(0, 0, 1, 2);
    // Row 0: transparent (gap)
    // Row 1: dark scanline
    sctx.fillStyle = `rgba(0, 0, 0, ${Math.min(1, this.config.scanlineAmount * 0.8)})`;
    sctx.fillRect(0, 1, 1, 1);

    // Pattern will be created per-frame from the main ctx (can't cross canvas contexts)
    this._scanlinePattern = null;
  }

  /**
   * Compute HSL hue for a beam at index i, blending colorStart → colorEnd
   * with a cycling offset for animation.
   */
  _getBeamColor(i, beamCount, amplitude) {
    const t = beamCount > 1 ? i / (beamCount - 1) : 0;
    const h1 = this._colorStartHSL;
    const h2 = this._colorEndHSL;

    // Interpolate hue with cycle offset
    let hue = h1.h + (h2.h - h1.h) * t + this.colorCycleOffset;
    hue = ((hue % 360) + 360) % 360;

    // Interpolate saturation
    const sat = h1.s + (h2.s - h1.s) * t;

    // Lightness modulated by amplitude and brightness config
    const baseLightness = h1.l + (h2.l - h1.l) * t;
    const lightness = Math.min(95, baseLightness * 0.4 + baseLightness * 0.6 * amplitude * (this.config.brightness / 0.8));

    return { hue, sat, lightness };
  }

  /**
   * Override draw() to produce trail fade that respects the background controller.
   *
   * Instead of overlaying a semi-transparent solid color (which ignores the global
   * background and always converges to that color), we:
   * 1. Save the current canvas (previous frame) to an offscreen buffer
   * 2. Draw the proper background via clearCanvas() → drawBackground()
   * 3. Composite the saved frame on top at reduced opacity (trail effect)
   * 4. Render new content
   *
   * This works correctly with solid colors, gradients, patterns, and images.
   */
  draw(frequencyData, features = null) {
    const elementCount = this.getElementCount();

    // Ensure buffer is properly sized
    if (!this._freqBuffer || this._freqBuffer.length !== elementCount) {
      this._freqBuffer = new Array(elementCount).fill(0);
    }

    // Map frequency data to element count
    const raw = mapFrequencyData(frequencyData, elementCount, this._freqBuffer);

    // Ensure previousAmplitudes is properly sized
    if (this.previousAmplitudes.length !== elementCount) {
      this.previousAmplitudes = new Array(elementCount).fill(0);
    }

    // Apply smoothing
    const amplitudes = smoothAmplitudes(raw, this.previousAmplitudes, this.config.smoothing);

    // Store features for convenience getters (bassEnergy, beatDetected, etc.)
    this._currentFeatures = features;

    const { ctx, canvas } = this;

    if (this.isFirstFrame) {
      this.clearCanvas();
      this.isFirstFrame = false;
    } else {
      // Trail effect via offscreen canvas compositing
      this._ensureTrailCanvas();

      // 1. Snapshot current canvas (previous frame) into offscreen buffer
      this._trailCtx.clearRect(0, 0, canvas.width, canvas.height);
      this._trailCtx.drawImage(canvas, 0, 0);

      // 2. Draw fresh background (respects background controller)
      this.clearCanvas();

      // 3. Composite previous frame on top at reduced opacity
      //    trailAlpha controls fade speed: higher = faster fade (less persistence)
      ctx.save();
      ctx.globalAlpha = 1 - this.config.trailAlpha;
      ctx.drawImage(this._trailCanvas, 0, 0);
      ctx.restore();
    }

    this.renderFrame(amplitudes, features);
  }

  /**
   * Main render: update rotation, draw haze, beams, flash, scanlines.
   */
  renderFrame(amplitudes, features) {
    const { ctx, canvas, config } = this;
    const beamCount = amplitudes.length;
    const originX = canvas.width / 2;
    const originY = canvas.height;
    const maxBeamLength = canvas.height * 0.95;
    const minBeamLength = canvas.height * 0.1;

    // --- Update rotation ---
    // Bass energy drives rotation speed
    const bassMultiplier = 1 + this.bassEnergy * 2;
    this.rotationOffset += config.rotationSpeed * 0.02 * bassMultiplier;

    // --- Beat snap ---
    if (this.beatDetected) {
      const snapStrength = config.snapAmount * (0.5 + this.beatConfidence * 0.5);
      // Alternate snap direction for variety
      const direction = Math.random() > 0.5 ? 1 : -1;
      this.snapOffset += snapStrength * direction * 0.5; // ~15-30 degrees in radians
    }
    // Exponential decay
    this.snapOffset *= SNAP_DECAY;

    // --- Color cycle ---
    this.colorCycleOffset += config.colorCycleSpeed * 2;

    // --- Layer 2: Haze ---
    if (config.hazeAmount > 0) {
      this._drawHaze(ctx, originX, originY, canvas.width, config.hazeAmount);
    }

    // --- Layer 3: Beams ---
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    const angleStep = beamCount > 1 ? FAN_SPREAD / (beamCount - 1) : 0;

    for (let i = 0; i < beamCount; i++) {
      const amp = amplitudes[i];
      const baseAngle = beamCount > 1
        ? FAN_START + i * angleStep
        : FAN_START + FAN_SPREAD / 2;

      // Add rotation, snap, and jitter
      const jitter = this.highEnergy * config.flicker * (Math.random() - 0.5) * 0.05;
      const angle = baseAngle + this.rotationOffset * 0.01 + this.snapOffset + jitter;

      // Beam length driven by amplitude
      const beamLength = minBeamLength + amp * maxBeamLength;

      // End point
      const endX = originX + Math.cos(angle) * beamLength;
      const endY = originY + Math.sin(angle) * beamLength;

      // Color for this beam
      const color = this._getBeamColor(i, beamCount, amp);
      const hsl = `hsl(${color.hue | 0}, ${color.sat | 0}%, ${color.lightness | 0}%)`;

      // Flicker: random per-beam alpha modulation driven by high frequency energy
      let beamAlpha = 0.7 + amp * 0.3;
      if (config.flicker > 0) {
        beamAlpha *= 1 - this.highEnergy * config.flicker * Math.random() * 0.4;
      }
      beamAlpha = Math.max(0.1, Math.min(1, beamAlpha));

      // --- Outer glow pass ---
      if (config.glowIntensity > 0) {
        ctx.globalAlpha = beamAlpha * 0.3;
        ctx.strokeStyle = hsl;
        ctx.lineWidth = config.beamWidth * 3 + amp * 4;
        ctx.shadowBlur = config.glowIntensity * 30 * amp;
        ctx.shadowColor = hsl;
        ctx.lineCap = 'round';
        ctx.beginPath();
        ctx.moveTo(originX, originY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      // --- Core pass ---
      ctx.globalAlpha = beamAlpha;
      ctx.strokeStyle = hsl;
      ctx.lineWidth = config.beamWidth;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(originX, originY);
      ctx.lineTo(endX, endY);
      ctx.stroke();
    }

    // --- Layer 4: Beat flash ---
    if (this.beatDetected) {
      ctx.globalAlpha = 0.6 + this.beatConfidence * 0.4;
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = Math.max(1, config.beamWidth * 0.6);
      ctx.lineCap = 'round';
      ctx.shadowBlur = 20;
      ctx.shadowColor = '#ffffff';

      for (let i = 0; i < beamCount; i++) {
        const amp = amplitudes[i];
        const baseAngle = beamCount > 1
          ? FAN_START + i * angleStep
          : FAN_START + FAN_SPREAD / 2;
        const angle = baseAngle + this.rotationOffset * 0.01 + this.snapOffset;
        const beamLength = minBeamLength + amp * maxBeamLength;
        const endX = originX + Math.cos(angle) * beamLength;
        const endY = originY + Math.sin(angle) * beamLength;

        ctx.beginPath();
        ctx.moveTo(originX, originY);
        ctx.lineTo(endX, endY);
        ctx.stroke();
      }
      ctx.shadowBlur = 0;
    }

    ctx.restore(); // Restore compositing mode

    // --- Layer 5: Scanlines ---
    if (config.scanlineAmount > 0) {
      this._drawScanlines(ctx, canvas.width, canvas.height);
    }
  }

  /**
   * Draw haze ellipses near the beam origin point.
   */
  _drawHaze(ctx, originX, originY, canvasWidth, hazeAmount) {
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';

    for (let i = 0; i < HAZE_COUNT; i++) {
      const h = this.hazePositions[i];
      const cx = originX + h.xOffset * canvasWidth;
      const cy = originY + h.yOffset * canvasWidth;
      const rx = h.radiusX * canvasWidth;
      const ry = h.radiusY * canvasWidth;

      // Haze alpha modulated by bass and config
      const alpha = hazeAmount * 0.15 * (0.5 + this.bassEnergy * 0.5);

      ctx.globalAlpha = alpha;
      ctx.fillStyle = 'rgba(45, 45, 76, 1)';
      ctx.beginPath();
      ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  /**
   * Draw scanline overlay using a pre-built pattern.
   */
  _drawScanlines(ctx, width, height) {
    // Lazily create pattern from main canvas context
    if (!this._scanlinePattern && this._scanlineCanvas) {
      this._scanlinePattern = ctx.createPattern(this._scanlineCanvas, 'repeat');
    }
    if (!this._scanlinePattern) return;

    ctx.save();
    ctx.globalAlpha = this.config.scanlineAmount;
    ctx.fillStyle = this._scanlinePattern;
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }

  /**
   * Clear canvas and reset all runtime state.
   */
  clear() {
    super.clear();
    this.rotationOffset = 0;
    this.snapOffset = 0;
    this.colorCycleOffset = 0;
    this.isFirstFrame = true;
    this._scanlinePattern = null;
    // Clear trail buffer so stale frames don't flash on restart
    if (this._trailCtx && this._trailCanvas) {
      this._trailCtx.clearRect(0, 0, this._trailCanvas.width, this._trailCanvas.height);
    }
  }
}

// --- Utility: hex color to HSL ---

function hexToHSL(hex) {
  if (!hex || hex.length < 7) return { h: 200, s: 80, l: 50 };

  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;

  if (isNaN(r) || isNaN(g) || isNaN(b)) return { h: 200, s: 80, l: 50 };

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = ((max + min) / 2) * 100;

  if (max === min) {
    return { h: 0, s: 0, l };
  }

  const d = max - min;
  const s = l > 50
    ? (d / (2 - max - min)) * 100
    : (d / (max + min)) * 100;

  let h;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
  else if (max === g) h = ((b - r) / d + 2) * 60;
  else h = ((r - g) / d + 4) * 60;

  return { h, s, l };
}

// Register with the visualizer registry
VisualizerRegistry.register(LaserFanRig);

export { LaserFanRig };
export default LaserFanRig;
